import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';
import Button from '../common/Button';

const PageContainer = styled.section`
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #121212 100%);
  color: #FFFFFF;
  min-height: 100vh;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  padding: 140px 24px 80px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(76, 175, 80, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    padding: 120px 24px 60px;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  text-align: center;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 64px;
  font-weight: 800;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FFD700 0%, #2196F3 50%, #FFFFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 24px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 48px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 32px;
  }
`;

const UseCasesSection = styled.section`
  padding: 80px 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 48px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 64px;
  color: #FFFFFF;
  
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 48px;
  }
`;

const UseCasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const UseCaseCard = styled(motion.div)`
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95) 0%, rgba(40, 40, 40, 0.8) 100%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(30px);
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(33, 150, 243, 0.03) 0%, 
      rgba(255, 215, 0, 0.03) 50%, 
      rgba(76, 175, 80, 0.03) 100%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }
  
  &:hover {
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 
      0 20px 60px rgba(33, 150, 243, 0.15),
      0 8px 32px rgba(255, 215, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateY(-8px) scale(1.02);
    
    &::before {
      opacity: 1;
    }
  }
  
  > * {
    position: relative;
    z-index: 2;
  }
`;

const CardHeader = styled.div`
  padding: 32px;
  text-align: center;
`;

const AnimatedTextContainer = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #FFFFFF;
  
  @media (max-width: 768px) {
    font-size: 20px;
    min-height: 68px;
  }
`;

const CardContent = styled.div`
  padding: 0 32px 32px;
`;

const CardDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 24px;
  text-align: center;
`;

const CardFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
`;

const CardFeature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  
  &::before {
    content: '✓';
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4CAF50 0%, #2196F3 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-right: 12px;
    font-size: 12px;
    flex-shrink: 0;
  }
`;

const CTASection = styled.section`
  padding: 120px 24px;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(255, 215, 0, 0.1) 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const CTAContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const CTATitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FFFFFF 0%, #2196F3 50%, #FFD700 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const CTADescription = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 48px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ContactInfo = styled.div`
  margin-top: 48px;
  padding-top: 48px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #FFD700;
`;

const ContactDetails = styled.div`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  
  a {
    color: #2196F3;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #FFD700;
    }
  }
`;

// Word-by-word animation component
const WordByWordText = ({ text, delay = 0, onComplete }) => {
  const [displayedWords, setDisplayedWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const words = text.split(' ');

  useEffect(() => {
    if (currentWordIndex < words.length) {
      const timeout = setTimeout(() => {
        setDisplayedWords(prev => [...prev, words[currentWordIndex]]);
        setCurrentWordIndex(prev => prev + 1);
      }, delay + currentWordIndex * 120);

      return () => clearTimeout(timeout);
    } else if (onComplete && currentWordIndex === words.length) {
      // Call onComplete after a small delay when all words are shown
      setTimeout(onComplete, 200);
    }
  }, [currentWordIndex, words, delay, onComplete]);

  return (
    <span>
      {displayedWords.map((word, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.4, 
            ease: "easeOut"
          }}
          style={{ 
            display: 'inline-block', 
            marginRight: '8px'
          }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
};

// Sequential text animation component
const SequentialText = ({ subtitle, title, delay = 0 }) => {
  const [showTitle, setShowTitle] = useState(false);

  const handleSubtitleComplete = () => {
    setShowTitle(true);
  };

  return (
    <>
      <div style={{ opacity: 0.7, fontSize: '0.85em', marginBottom: '4px' }}>
        <WordByWordText 
          text={subtitle} 
          delay={delay} 
          onComplete={handleSubtitleComplete}
        />
      </div>
      {showTitle && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <WordByWordText text={title} delay={0} />
        </motion.div>
      )}
    </>
  );
};

const Business = () => {
  useEffect(() => {
    trackPageView('Business', window.location.pathname);
  }, []);

  const useCases = [
    {
      id: 1,
      title: 'Lead Generation',
      subtitle: 'Drive traffic on autopilot',
      description: 'Embed CarConsul widget to convert visitors into qualified buyers automatically.',
      features: [
        'White-label widget',
        'Lead capture automation',
        'Real-time analytics',
        'Custom branding',
        'API integration'
      ]
    },
    {
      id: 2,
      title: 'Customer Retention',
      subtitle: 'Bring customers back',
      description: 'Re-engage previous customers with personalized vehicle recommendations.',
      features: [
        'Database integration',
        'Retention campaigns',
        'Vehicle alerts',
        'Trade-in notifications',
        'Lifecycle tracking'
      ]
    },
    {
      id: 3,
      title: 'Enterprise Integration',
      subtitle: 'Fleet management solutions',
      description: 'Custom integrations for dealerships and fleet managers.',
      features: [
        'Bulk VIN processing',
        'Custom endpoints',
        'Advanced reporting',
        'Multi-location support',
        'Dedicated support'
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="CarConsul for Business - Enterprise Vehicle Analysis Solutions"
        description="Transform your automotive business with AI-powered vehicle analysis. Lead generation, customer retention, and enterprise solutions."
        pagePath="business"
      />
      
      <PageContainer>
        <HeroSection>
          <HeroContent
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              CarConsul for Business
            </HeroTitle>
            <HeroSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Transform your automotive business with AI-powered vehicle analysis. 
              Drive leads, retain customers, and scale with enterprise-grade solutions.
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <UseCasesSection>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Enterprise Use Cases
          </SectionTitle>
          
          <UseCasesGrid>
            {useCases.map((useCase, index) => (
              <UseCaseCard
                key={useCase.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <CardHeader>
                  <AnimatedTextContainer>
                    <SequentialText 
                      subtitle={useCase.subtitle}
                      title={useCase.title}
                      delay={index * 400}
                    />
                  </AnimatedTextContainer>
                </CardHeader>
                
                <CardContent>
                  <CardDescription>{useCase.description}</CardDescription>
                  
                  <CardFeatures>
                    {useCase.features.map((feature, idx) => (
                      <CardFeature key={idx}>{feature}</CardFeature>
                    ))}
                  </CardFeatures>
                  
                  <Button
                    variant="primary"
                    size="medium"
                    style={{ 
                      width: '100%',
                      background: 'linear-gradient(135deg, #2196F3 0%, #FFD700 100%)',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                    onClick={() => window.location.href = `mailto:nick@carconsul.com?subject=CarConsul Business - ${useCase.title} Inquiry`}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </UseCaseCard>
            ))}
          </UseCasesGrid>
        </UseCasesSection>

        <CTASection>
          <CTAContent
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CTATitle>Ready to Scale Your Business?</CTATitle>
            <CTADescription>
              Join leading automotive companies using CarConsul to drive growth, 
              improve customer experience, and increase revenue.
            </CTADescription>
            
            <CTAButtons>
              <Button
                variant="primary"
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #FFD700 0%, #2196F3 100%)',
                  padding: '16px 32px',
                  fontSize: '18px',
                  fontWeight: '600'
                }}
                onClick={() => window.location.href = 'mailto:nick@carconsul.com?subject=CarConsul Business Partnership'}
              >
                Schedule Demo
              </Button>
              
              <Button
                variant="secondary"
                size="large"
                style={{
                  border: '2px solid #2196F3',
                  color: '#2196F3',
                  backgroundColor: 'transparent',
                  padding: '14px 32px',
                  fontSize: '18px',
                  fontWeight: '600'
                }}
                onClick={() => window.location.href = 'https://app.carconsul.com'}
              >
                Try CarConsul
              </Button>
            </CTAButtons>

            <ContactInfo>
              <ContactTitle>Enterprise Sales</ContactTitle>
              <ContactDetails>
                Email: <a href="mailto:nick@carconsul.com">nick@carconsul.com</a>
                <br />
                Let's discuss custom solutions for your business
              </ContactDetails>
            </ContactInfo>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

export default Business;